/* custom code start */
/**
 * BBODY – Bundler sectioned mix & match steps as dropdowns.
 * Styling lives in component-bundler.css; this adds the behaviour CSS can't:
 * - writes the chosen product name(s) into each step header, like a filled select
 * - a single-choice step with one product works as a checkbox (tick = add, untick = remove)
 * - picking another option in a full single-choice step swaps the choice
 * - marks the chosen option; after a pick that fills the step, opens the next
 *   empty step or closes the dropdown
 * It only clicks Bundler's own buttons, so Bundler keeps handling the bundle,
 * the discount and add to cart. Only sectioned bundles are affected; steps that
 * allow more than one product keep Bundler's normal behaviour.
 */
(() => {
  const MAIN = '.bndlr-sectioned-mixnmatch .bndlr-sections-main-container';
  const ACTIVE_ATTR = 'data-bundler-active-section';
  const CLOSED = '-1';

  const getSection = (main, id) => main.querySelector(`.bndlr-sectioned-section[data-bundler-section="${id}"]`);
  const getStatus = (main, id) => main.querySelector(`.bndlr-sectioned-section-status[data-bundler-section-status="${id}"]`);
  const getOptions = (section) =>
    section ? [...section.querySelectorAll('.bndlr-sectioned-available-products .bndlr-product')] : [];
  const getChosen = (status) => [...status.querySelectorAll('.bndlr-sectioned-status-box-product')];
  const getAddButton = (option) => option.querySelector('.bndlr-add-to-sectioned-bundle');
  const getVariantId = (option) => option.querySelector('select.bndlr-select-variant')?.value;
  const isFull = (section) =>
    getOptions(section).every((option) => getAddButton(option)?.classList.contains('bndlr-hidden'));

  // Bundle config from Bundler's public API, cached once it has loaded
  let bundles = null;
  const getBundles = () => {
    if (!bundles && typeof window.bndlr?.getBundles === 'function') {
      const loaded = window.bndlr.getBundles();
      if (Array.isArray(loaded) && loaded.length > 0) bundles = loaded;
    }
    return bundles || [];
  };

  // A step is single-choice only when Bundler is configured with max 1 for it
  const isSingleChoice = (main, id) => {
    const bundleId = main.closest('[data-bundle]')?.getAttribute('data-bundle');
    const bundle = getBundles().find((item) => String(item.id) === bundleId);
    const step = bundle?.sections?.[Number(id)];
    return step ? Number(step.max_items) === 1 : false;
  };

  const setClass = (element, className, on) => {
    if (element.classList.contains(className) !== on) element.classList.toggle(className, on);
  };

  // Text of an element without Bundler's screen-reader-only labels ("Current price: ")
  const visibleText = (element) =>
    element
      ? [...element.childNodes]
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent)
          .join('')
          .trim()
      : '';

  const chosenLabel = (chosenProduct) => {
    const title = chosenProduct.querySelector('img')?.getAttribute('title') || '';
    const quantity = chosenProduct.querySelector('.bndlr-sectioned-status-box-product-quantity');
    return quantity ? `${quantity.textContent.trim()} ${title}` : title;
  };

  const syncMain = (main) => {
    main.querySelectorAll('.bndlr-sectioned-section-status').forEach((status) => {
      const id = status.getAttribute('data-bundler-section-status');
      const section = getSection(main, id);
      const options = getOptions(section);
      const chosen = getChosen(status);
      const isCheckbox = options.length === 1 && isSingleChoice(main, id);

      setClass(status, 'bx-step--single', isSingleChoice(main, id));
      setClass(status, 'bx-step--checkbox', isCheckbox);
      if (section) setClass(section, 'bx-step--checkbox', isCheckbox);

      const name = status.querySelector('.bndlr-sectioned-section-name');
      if (!name) return;

      let value = name.querySelector('.bx-step-value');
      if (!value) {
        value = document.createElement('span');
        value.className = 'bx-step-value';
        name.appendChild(value);
      }

      const text = isCheckbox
        ? visibleText(options[0].querySelector('.bndlr-new-price'))
        : chosen.map(chosenLabel).join(', ');
      if (value.textContent !== text) value.textContent = text;
      setClass(status, 'bx-step--has-value', !isCheckbox && text !== '');

      const chosenVariantIds = chosen.map((product) => product.getAttribute('data-variant-id'));
      options.forEach((option) => {
        setClass(option, 'bx-option--selected', chosenVariantIds.includes(getVariantId(option)));
        if (!option.hasAttribute('tabindex')) option.setAttribute('tabindex', '0');
      });
    });
  };

  let syncQueued = false;
  const syncAll = () => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      document.querySelectorAll(MAIN).forEach(syncMain);
    });
  };

  // Removes everything chosen in a step. Bundler redraws the list on every removal.
  const clearStep = (status) => {
    let guard = 20;
    let close;
    while (guard-- > 0 && (close = status.querySelector('.bndlr-sectioned-status-box-product .bndlr-close'))) {
      close.click();
    }
  };

  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleHeader = (event, main, header) => {
    const status = header.closest('.bndlr-sectioned-section-status');
    if (!status) return;
    const id = status.getAttribute('data-bundler-section-status');

    if (status.classList.contains('bx-step--checkbox')) {
      stop(event);
      if (getChosen(status).length > 0) {
        clearStep(status);
      } else {
        const [option] = getOptions(getSection(main, id));
        if (option) getAddButton(option)?.click();
      }
      return;
    }

    // Clicking the open dropdown's header closes it
    if (main.getAttribute(ACTIVE_ATTR) === id) {
      stop(event);
      main.setAttribute(ACTIVE_ATTR, CLOSED);
    }
  };

  // Only reached when the row has no visible add button, i.e. the step is full
  const handleOption = (event, main, option) => {
    stop(event);
    const section = option.closest('.bndlr-sectioned-section');
    const button = getAddButton(option);
    if (!section || !button) return;
    const id = section.getAttribute('data-bundler-section');

    // Multi-product steps keep Bundler's behaviour: remove a pick before adding another
    if (!isSingleChoice(main, id)) return;

    if (option.classList.contains('bx-option--selected')) {
      main.setAttribute(ACTIVE_ATTR, CLOSED);
      return;
    }

    const status = getStatus(main, id);
    if (status && button.classList.contains('bndlr-hidden')) clearStep(status);
    button.click();
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const main = target.closest(MAIN);
      if (!main) return;

      const header = target.closest('.bndlr-sectioned-section-name');
      if (header) {
        handleHeader(event, main, header);
        return;
      }

      const option = target.closest('.bndlr-sectioned-available-products .bndlr-product');
      if (option && !target.closest('.bndlr-add-to-sectioned-bundle, select')) handleOption(event, main, option);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target;
      if (!(target instanceof Element) || !target.matches('.bndlr-sectioned-available-products .bndlr-product')) return;
      const main = target.closest(MAIN);
      if (!main) return;
      const button = getAddButton(target);
      if (button && !button.classList.contains('bndlr-hidden') && !target.classList.contains('bx-option--selected')) {
        stop(event);
        button.click();
        return;
      }
      handleOption(event, main, target);
    },
    true
  );

  // After a pick that fills the open step, open the next empty step or close the dropdown.
  // Runs after Bundler's own "move to next section" (50ms), so both settings end the same way.
  document.addEventListener('bndlr:sectioned_mixnmatch:product_added', () => {
    setTimeout(() => {
      document.querySelectorAll(MAIN).forEach((main) => {
        const section = getSection(main, main.getAttribute(ACTIVE_ATTR));
        if (!section || !isFull(section)) return;
        const next = main.querySelector(
          '.bndlr-sectioned-section[data-requirements-fulfilled="false"]:not(.bx-step--checkbox)'
        );
        main.setAttribute(ACTIVE_ATTR, next ? next.getAttribute('data-bundler-section') : CLOSED);
      });
    }, 150);
  });

  new MutationObserver(syncAll).observe(document.documentElement, { childList: true, subtree: true });
  syncAll();
})();
/* custom code end */
